"use strict";
// Feature: address-geolocation, Task 5.3
//
// applyLocationUpdate — pure coordinate-stability helper.
//
// Enforces requirement 4.x: stored `latitude`/`longitude` change ONLY on an
// explicit trigger (typed edit, pin drag, or explicit re-geocode). Address-
// only updates and unrelated-field updates never touch coordinates. Reverse-
// geocode display never mutates coordinates.
//
// This module is intentionally I/O-free — Lambdas call it to compute the
// next `GeoLocation` state and then persist that state. No side effects,
// no clock, no randomness. The caller injects `now` (ISO 8601 string).
Object.defineProperty(exports, "__esModule", { value: true });
exports.applyLocationUpdate = void 0;
const models_1 = require("../models");
/**
 * Compute the next `GeoLocation` state after an operation.
 *
 * Rules:
 *   - No trigger → coordinates unchanged (returns `current`).
 *   - Trigger with invalid coordinates → coordinates unchanged, `error`
 *     surfaced so the caller can respond (Req 3.3).
 *   - Trigger with valid coordinates → new `GeoLocation` at those coords,
 *     `source` set from trigger kind, `updatedAt` set to `now`.
 *
 * Pure. Never throws. Never mutates `current`.
 */
function applyLocationUpdate(input) {
    const { current, trigger, now, markValidated } = input;
    if (!trigger)
        return { changed: false, next: current };
    const validation = (0, models_1.validateCoordinate)(trigger.latitude, trigger.longitude);
    if (!validation.ok) {
        return { changed: false, next: current, error: "invalid-coordinate" };
    }
    const source = trigger.kind === "regeocode" ? "geocoded" : "manual";
    const next = {
        latitude: validation.latitude,
        longitude: validation.longitude,
        ...(trigger.kind === "regeocode" && trigger.accuracyMetres !== undefined
            ? { accuracyMetres: trigger.accuracyMetres }
            : {}),
        source,
        validated: markValidated ??
            (trigger.kind === "regeocode" ? true : false),
        updatedAt: now,
    };
    return { changed: true, next };
}
exports.applyLocationUpdate = applyLocationUpdate;
